// Sending a dashboard or config from the browser, as TUNE Live sends one: what it is, read from the file itself (a
// dashboard's theme.json, a config's root element), its SHA-256, then its parts at the addresses the API gives.
import type { components } from '@tune/shared';
import { ApiError, data, type Api } from './api';

type UploadDone = components['schemas']['UploadDone'];

export interface Described {
  kind: 'dashboard' | 'config';
  /** A dashboard's meta.id, or a config's id: the same item through every version. */
  identity: string;
  name: string;
}

/** A file the library can't take, and why, in words for the page. */
export class NotAnItem extends Error {}

const ZIP_END = 0x06054b50;
const ZIP_ENTRY = 0x02014b50;
const ZIP_LOCAL = 0x04034b50;

/** One file from a ZIP archive, stored or deflated. */
async function zipEntry(bytes: Uint8Array, wanted: string): Promise<Uint8Array> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let end = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 22 - 65_535); i--) {
    if (view.getUint32(i, true) === ZIP_END) {
      end = i;
      break;
    }
  }
  if (end < 0) {
    throw new NotAnItem("This dashboard isn't a ZIP file as TUNE Live writes one.");
  }
  const count = view.getUint16(end + 10, true);
  let at = view.getUint32(end + 16, true);
  for (let n = 0; n < count; n++) {
    if (at + 46 > bytes.length || view.getUint32(at, true) !== ZIP_ENTRY) {
      throw new NotAnItem("This dashboard's ZIP file is damaged.");
    }
    const method = view.getUint16(at + 10, true);
    const compressed = view.getUint32(at + 20, true);
    const nameLength = view.getUint16(at + 28, true);
    const extra = view.getUint16(at + 30, true);
    const comment = view.getUint16(at + 32, true);
    const local = view.getUint32(at + 42, true);
    const name = new TextDecoder().decode(bytes.subarray(at + 46, at + 46 + nameLength));
    if (name === wanted) {
      if (view.getUint32(local, true) !== ZIP_LOCAL) {
        throw new NotAnItem("This dashboard's ZIP file is damaged.");
      }
      const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
      const raw = bytes.subarray(start, start + compressed);
      if (method === 0) {
        return raw;
      }
      if (method === 8) {
        const stream = new Blob([new Uint8Array(raw)]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
        return new Uint8Array(await new Response(stream).arrayBuffer());
      }
      throw new NotAnItem("This dashboard's theme.json is packed in a way TUNE Live doesn't.");
    }
    at += 46 + nameLength + extra + comment;
  }
  throw new NotAnItem('This dashboard has no theme.json.');
}

const attribute = (attributes: string, name: string): string | null => {
  const found = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`).exec(attributes);
  return found ? (found[1] ?? found[2] ?? null) : null;
};

const unescapeXml = (text: string) =>
  text
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

/** What a file is: a dashboard (a .tunetheme ZIP) or a config (a .tuneconfig), with its identity and name. */
export async function describeFile(file: File): Promise<Described> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
    let theme: { meta?: { id?: unknown; name?: unknown; version?: unknown; minAppVersion?: unknown } };
    try {
      theme = JSON.parse(new TextDecoder().decode(await zipEntry(bytes, 'theme.json'))) as typeof theme;
    } catch (e) {
      throw e instanceof NotAnItem ? e : new NotAnItem("This dashboard's theme.json doesn't read.");
    }
    const { id, name, version, minAppVersion } = theme.meta ?? {};
    if (typeof id !== 'string' || typeof name !== 'string' || !id || !name) {
      throw new NotAnItem("This dashboard's theme.json has no id or name.");
    }
    // As TUNE's cloud and TUNE Live's theme validator check (server/src/library/describe.ts): two or three numbers.
    const isVersion = (v: unknown) => typeof v === 'string' && /^\d+\.\d+(\.\d+)?$/.test(v);
    if (!isVersion(version) || !isVersion(minAppVersion)) {
      throw new NotAnItem(
        "This dashboard's theme.json doesn't say its version and the oldest TUNE Live it works on, as numbers like 1.2.0.",
      );
    }
    return { kind: 'dashboard', identity: id, name };
  }
  const text = new TextDecoder().decode(bytes.subarray(0, 64 * 1024));
  const root = /<CanDefinition\b([^>]*)>/.exec(text);
  if (!root) {
    throw new NotAnItem("This isn't a dashboard or a config.");
  }
  const id = attribute(root[1] ?? '', 'id');
  if (!id) {
    throw new NotAnItem('This config has no id. TUNE Live gives it one when it sends it, or tune-convert does.');
  }
  const name = attribute(root[1] ?? '', 'name');
  return {
    kind: 'config',
    identity: unescapeXml(id),
    name: name ? unescapeXml(name) : file.name.replace(/\.[^.]*$/, ''),
  };
}

export async function sha256(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Sends a library version: the next version of `itemId`, or a new item. The answer is the version made, or the one
 * the account had already with these bytes.
 */
export async function sendVersion(
  api: Api,
  file: File,
  what: { itemId: string } | { newItem: Described },
  progress?: (sent: number, total: number) => void,
): Promise<{ id: string; n: number }> {
  const start =
    'itemId' in what
      ? { purpose: 'libraryVersion' as const, itemId: what.itemId }
      : {
          purpose: 'libraryVersion' as const,
          newItem: { kind: what.newItem.kind, identity: what.newItem.identity, name: what.newItem.name },
        };
  const begun = await data(
    api.POST('/uploads', { body: { ...start, size: file.size, sha256: await sha256(file), fileName: file.name } }),
  );
  if ('existing' in begun) {
    return { id: begun.existing.itemId, n: begun.existing.n };
  }
  const { uploadId, partSize, partCount } = begun;
  let sent = 0;
  for (let first = 1; first <= partCount; first += 100) {
    const numbers = Array.from({ length: Math.min(100, partCount - first + 1) }, (_, i) => first + i);
    const { parts } = await data(
      api.POST('/uploads/{uploadId}/parts', { params: { path: { uploadId } }, body: { numbers } }),
    );
    for (const part of parts) {
      const from = (part.n - 1) * partSize;
      const res = await fetch(part.url, { method: 'PUT', body: file.slice(from, from + part.size) });
      if (!res.ok) {
        throw new ApiError(res.status, 'part_refused', `a part answered ${res.status}`);
      }
      sent += part.size;
      progress?.(sent, file.size);
    }
  }
  const done: UploadDone = await data(
    api.POST('/uploads/{uploadId}/complete', { params: { path: { uploadId } }, body: {} }),
  );
  if (!('item' in done)) {
    throw new ApiError(500, 'unknown', 'the upload became something else');
  }
  return done.item;
}
